// src/products/dataroom/hooks/useProjectManagement.js
// Custom hook for managing Dataroom projects

import { useState, useEffect, useCallback } from 'react';
import projectService from '../services/projectService';

export const useProjectManagement = (userId) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ensure projects is always an array
  const safeProjects = projects || [];

  // Load user's projects
  const loadProjects = useCallback(async (targetUserId) => {
    const userIdToLoad = targetUserId || userId;
    if (!userIdToLoad) {
      console.log('No userId provided to loadProjects');
      return;
    }

    try {
      console.log('Loading projects for userId:', userIdToLoad);
      setLoading(true);
      setError(null);

      const result = await projectService.getUserProjects(userIdToLoad);
      if (result.error) {
        console.error('Project service returned error:', result.error);
        throw result.error;
      }

      console.log('Projects loaded successfully:', result.data);
      setProjects(result.data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError(err.message);
      // Set empty array to prevent undefined errors
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [userId]); // ✅ Removed loading dependency to prevent circular dependency

  // Create new project
  const createProject = useCallback(async (projectData) => {
    try {
      setError(null);

      const result = await projectService.createProject(projectData);
      if (result.error) {
        throw result.error;
      }

      // Add to local state
      setProjects(prev => [result.data, ...prev]);
      setSelectedProject(result.data);

      return result.data;
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Update project
  const updateProject = useCallback(async (projectId, projectData) => {
    try {
      setError(null);

      const result = await projectService.updateProject(projectId, projectData);
      if (result.error) {
        throw result.error;
      }

      // Update local state
      setProjects(prev => prev.map(project => 
        project.id === projectId ? { ...project, ...result.data } : project
      ));

      // Update selected project if it's the one being updated
      if (selectedProject && selectedProject.id === projectId) {
        setSelectedProject(result.data);
      }

      return result.data;
    } catch (err) {
      console.error('Error updating project:', err);
      setError(err.message);
      throw err;
    }
  }, [selectedProject]);

  // Delete project
  const deleteProject = useCallback(async (projectId) => {
    try {
      setError(null);

      const result = await projectService.deleteProject(projectId);
      if (result.error) {
        throw result.error;
      }

      // Remove from local state
      setProjects(prev => prev.filter(project => project.id !== projectId));

      // Clear selected project if it was deleted
      if (selectedProject && selectedProject.id === projectId) {
        setSelectedProject(null);
      }

      return result.data;
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err.message);
      throw err;
    }
  }, [selectedProject]);

  // Toggle project status
  const toggleProjectStatus = useCallback(async (projectId, status) => {
    try {
      setError(null);

      const result = await projectService.toggleProjectStatus(projectId, status);
      if (result.error) {
        throw result.error;
      }

      // Update local state
      setProjects(prev => prev.map(project => 
        project.id === projectId ? { ...project, status } : project
      ));

      return result.data;
    } catch (err) {
      console.error('Error toggling project status:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Get project statistics
  const getProjectStats = useCallback(async (projectId) => {
    try {
      const result = await projectService.getProjectStats(projectId);
      if (result.error) {
        throw result.error;
      }

      return result.data;
    } catch (err) {
      console.error('Error fetching project stats:', err);
      throw err;
    }
  }, []);

  // Load data on mount and when userId changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (userId) {
      loadProjects();
    }
  }, [userId, loadProjects]); // Remove loadProjects dependency to prevent infinite loop

  return {
    // State
    projects: safeProjects,
    selectedProject,
    loading,
    error,

    // Actions
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
    toggleProjectStatus,
    getProjectStats,
    setSelectedProject,

    // Computed values
    hasProjects: safeProjects.length > 0,
    activeProjects: safeProjects.filter(p => p.status === 'active'),
    archivedProjects: safeProjects.filter(p => p.status === 'archived'),
    draftProjects: safeProjects.filter(p => p.status === 'draft')
  };
};

export default useProjectManagement;
